import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Plus, Trash2, Wrench } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { API } from "../../App";

const money = (v) => `€${Number(v || 0).toFixed(2)}`;

/**
 * Change services / price on a live booking.
 *
 * The customer is standing at the counter, so the dialog shows the new
 * balance BEFORE anything is written: every keystroke re-runs the
 * server-side preview, and Save commits exactly that plan. No figure is
 * computed in the browser.
 */
export const AdjustServicesDialog = ({ booking, open, onClose, onSaved }) => {
  const bookingId = booking?.booking_id;
  const [catalogue, setCatalogue] = useState([]);
  const [selected, setSelected] = useState([]);
  const [customLines, setCustomLines] = useState([]);
  const [agreedTotal, setAgreedTotal] = useState("");
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(true);
  const [promoDecision, setPromoDecision] = useState(null);
  const [preview, setPreview] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(booking?.service_ids || []);
    setCustomLines([]);
    setAgreedTotal("");
    setReason("");
    setNotify(true);
    setPromoDecision(null);
    setPreview(null);
    setBlocked(null);
    axios.get(`${API}/services/`)
      .then((r) => setCatalogue(r.data || []))
      .catch(() => setCatalogue([]));
  }, [open, booking]);

  const body = useCallback(() => ({
    service_ids: selected,
    custom_line_items: customLines
      .filter((l) => l.name.trim() && Number(l.price) > 0)
      .map((l) => ({ name: l.name.trim(), price: Number(l.price) })),
    agreed_total: agreedTotal === "" ? null : Number(agreedTotal),
    reason,
    promo_decision: promoDecision,
    notify_customer: notify,
  }), [selected, customLines, agreedTotal, reason, promoDecision, notify]);

  // Debounced server-side preview — the single source of every number here.
  useEffect(() => {
    if (!open || !bookingId) return;
    const payload = body();
    if (!payload.service_ids.length && !payload.custom_line_items.length) {
      setPreview(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await axios.post(
          `${API}/auth/bookings/${bookingId}/adjust-services/preview`, payload);
        setPreview(r.data);
        setBlocked(null);
      } catch (err) {
        setPreview(null);
        const d = err.response?.data?.detail;
        setBlocked(typeof d === "object" ? d : { message: d || "Preview failed" });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [open, bookingId, body]);

  const toggleService = (sid) =>
    setSelected((s) => (s.includes(sid) ? s.filter((x) => x !== sid) : [...s, sid]));

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/auth/bookings/${bookingId}/adjust-services`, body());
      if (r.data.already_applied) {
        toast.info("That change was already applied — nothing to do");
      } else {
        toast.success(
          `Updated — total ${money(r.data.new_total)}, ${money(r.data.new_balance)} due on collection`);
      }
      onSaved?.(r.data);
      onClose?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (typeof d === "object" && d?.code === "promo_ineligible") {
        setBlocked(d);
        toast.error(d.message);
      } else {
        toast.error(typeof d === "object" ? d.message : (d || "Failed to save"));
      }
    } finally { setSaving(false); }
  };

  const needsPromoDecision = blocked?.code === "promo_ineligible";
  const canSave = !!preview && !preview.reason_required && !saving
    && !(needsPromoDecision && !promoDecision);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) onClose?.(); }}>
      <DialogContent
        className="bg-slate-900 border-slate-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="adjust-services-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-saturn-orange" /> Change services / price
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            {booking?.customer_name} · {money(booking?.total_price)} booked.
            Pick the work actually agreed — the balance, job card, calendar and
            invoice all follow.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {/* ---- Services ------------------------------------------------ */}
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wide">Services</Label>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-48 overflow-y-auto pr-1">
              {catalogue.map((s) => (
                <label key={s.service_id}
                       className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-800/60 cursor-pointer">
                  <input type="checkbox" checked={selected.includes(s.service_id)}
                         onChange={() => toggleService(s.service_id)}
                         data-testid={`adjust-service-${s.service_id}`} />
                  <span className="flex-1 truncate text-slate-200">{s.name}</span>
                  <span className="text-slate-400 text-xs">
                    {money((s.price_by_size || {})[
                      (booking?.vehicle?.size || "saloon")] ?? s.base_price)}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* ---- Custom lines -------------------------------------------- */}
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-slate-300 text-xs uppercase tracking-wide">
                One-off lines
              </Label>
              <Button size="sm" variant="outline"
                      className="h-7 border-slate-600 text-slate-300"
                      onClick={() => setCustomLines((l) => [...l, { name: "", price: "" }])}
                      data-testid="adjust-add-custom-line">
                <Plus className="w-3 h-3 mr-1" /> Add line
              </Button>
            </div>
            {customLines.map((line, i) => (
              <div key={i} className="flex gap-2 mt-2">
                <Input placeholder="e.g. Partial glass" value={line.name}
                       onChange={(e) => setCustomLines((l) =>
                         l.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                       className="bg-slate-800 border-slate-700 text-white"
                       data-testid={`adjust-custom-name-${i}`} />
                <Input type="number" step="0.01" placeholder="0.00" value={line.price}
                       onChange={(e) => setCustomLines((l) =>
                         l.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)))}
                       className="bg-slate-800 border-slate-700 text-white w-28"
                       data-testid={`adjust-custom-price-${i}`} />
                <Button size="icon" variant="ghost"
                        className="text-slate-400 hover:text-red-400"
                        onClick={() => setCustomLines((l) => l.filter((_, j) => j !== i))}
                        data-testid={`adjust-remove-custom-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>

          {/* ---- Agreed total + reason ----------------------------------- */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300 text-xs">
                Agreed total (optional override)
              </Label>
              <Input type="number" step="0.01" value={agreedTotal}
                     placeholder={preview ? String(preview.new.computed_total) : "computed"}
                     onChange={(e) => setAgreedTotal(e.target.value)}
                     className="bg-slate-800 border-slate-700 text-white mt-1"
                     data-testid="adjust-agreed-total" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs">
                Reason {preview?.reason_required && (
                  <span className="text-amber-400">· required</span>)}
              </Label>
              <Textarea rows={2} value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. Customer reduced scope on arrival"
                        className="bg-slate-800 border-slate-700 text-white mt-1"
                        data-testid="adjust-reason" />
            </div>
          </div>

          {/* ---- Promo decision ----------------------------------------- */}
          {needsPromoDecision && (
            <div className="rounded border border-amber-500/40 bg-amber-500/10 p-3 space-y-2"
                 data-testid="adjust-promo-decision">
              <p className="text-amber-200 flex gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                {blocked.message}
              </p>
              <div className="flex gap-2">
                <Button size="sm"
                        variant={promoDecision === "keep" ? "default" : "outline"}
                        className={promoDecision === "keep"
                          ? "bg-emerald-600 text-white"
                          : "border-slate-600 text-slate-300"}
                        onClick={() => setPromoDecision("keep")}
                        data-testid="adjust-promo-keep">Keep the discount</Button>
                <Button size="sm"
                        variant={promoDecision === "remove" ? "default" : "outline"}
                        className={promoDecision === "remove"
                          ? "bg-red-600 text-white"
                          : "border-slate-600 text-slate-300"}
                        onClick={() => setPromoDecision("remove")}
                        data-testid="adjust-promo-remove">Remove it</Button>
              </div>
            </div>
          )}

          {/* ---- Blocked (anything else) -------------------------------- */}
          {blocked && !needsPromoDecision && (
            <p className="rounded border border-red-500/40 bg-red-500/10 p-2 text-red-200"
               data-testid="adjust-blocked">{blocked.message}</p>
          )}

          {/* ---- Where the booking stands RIGHT NOW --------------------- */}
          {/* Shown whenever the live preview isn't available. The preview
              totals the TICKED services at catalogue price, so a booking
              carrying an agreed price (a EUR 2450 job whose services list
              EUR 310) opens with a refused preview and used to show the
              admin no figures at all — not even the balance they came in
              to read. Straight off the booking, never recomputed. */}
          {!preview && (
            <div className="rounded border border-slate-700 bg-slate-800/50 p-3"
                 data-testid="adjust-current-figures">
              <p className="text-[10px] uppercase text-slate-500 mb-1">As it stands</p>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                <span className="text-slate-300">
                  {money(booking?.total_price)} total
                </span>
                <span className="text-slate-400">
                  deposit paid{" "}
                  <span data-testid="adjust-current-deposit">
                    {money(booking?.deposit_amount)}
                  </span>
                </span>
                <span className="text-saturn-orange font-semibold"
                      data-testid="adjust-current-balance">
                  {money(booking?.balance_amount ?? booking?.remaining_balance_due)}
                  {" "}due on collection
                </span>
              </div>
            </div>
          )}

          {/* ---- Live preview ------------------------------------------- */}
          {preview && (
            <div className="rounded border border-slate-700 bg-slate-800/50 p-3 space-y-2"
                 data-testid="adjust-preview">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] uppercase text-slate-500">Before</p>
                  <p className="text-slate-300">{money(preview.old.total_price)} total</p>
                  <p className="text-slate-400 text-xs">
                    {money(preview.old.balance_due)} balance
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-slate-500">After</p>
                  <p className="text-white font-semibold" data-testid="adjust-new-total">
                    {money(preview.new.total_price)} total
                  </p>
                  <p className="text-saturn-orange font-semibold"
                     data-testid="adjust-new-balance">
                    {money(preview.new.balance_due)} due on collection
                  </p>
                </div>
              </div>
              <ul className="text-xs text-slate-300 space-y-0.5 border-t border-slate-700 pt-2">
                {preview.new.services.map((s, i) => (
                  <li key={i} className="flex justify-between">
                    <span className="truncate">{s.name}</span>
                    <span>{money(s.price)}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-slate-400">
                Deposit paid {money(preview.deposit_credited)} ·{" "}
                {preview.new.duration_minutes} min
                {preview.promo?.new_discount > 0 && (
                  <> · discount {money(preview.promo.new_discount)}</>)}
              </p>
              {preview.warnings?.map((w, i) => (
                <p key={i} className="text-xs text-amber-300"
                   data-testid={`adjust-warning-${i}`}>{w}</p>
              ))}
            </div>
          )}

          <label className="flex items-center gap-2 text-xs text-slate-300">
            <input type="checkbox" checked={notify}
                   onChange={(e) => setNotify(e.target.checked)}
                   data-testid="adjust-notify-customer" />
            Email the customer the updated services and balance
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}
                  className="border-slate-700 text-slate-300"
                  data-testid="adjust-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={!canSave}
                  className="bg-saturn-orange hover:bg-saturn-orange/80 text-black font-semibold"
                  data-testid="adjust-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" />
              : preview ? `Save — ${money(preview.new.balance_due)} due` : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdjustServicesDialog;
